import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateScheduledGiftRequest,
  FetchScheduledGiftRecipientsRequest,
  FetchScheduledGiftRecipientsResponse,
  FetchScheduledGiftsRequest,
  FetchScheduledGiftsResponse,
  Individual,
  individualsService,
  ScheduledGift,
  ScheduledGiftRecipientOutcome,
  scheduledGiftsService,
  UpdateScheduledGiftRequest,
} from '@aslanpay/portal-common-lib';

// Recipients and people are looked up a page at a time, never one call per person.
export const RECIPIENT_LOOKUP_PAGE_SIZE = 100;

// How often a gift still sending is checked again, so the screen ends on how it went.
export const PROCESSING_REFRESH_MS = 5000;

// The most employees the gift flow pre-selects, when editing a gift or sending one again; more stay fixed or are
// refused rather than loading them all into the browser.
export const PRESELECTED_RECIPIENTS_LIMIT = 1000;

const useInvalidateScheduledGifts = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['fetchScheduledGifts'] });
    queryClient.invalidateQueries({ queryKey: ['fetchScheduledGift'] });
    queryClient.invalidateQueries({ queryKey: ['fetchScheduledGiftRecipients'] });
  };
};

const useFetchScheduledGifts = (params: FetchScheduledGiftsRequest, enabled = true) => {
  return useQuery<FetchScheduledGiftsResponse, Error>({
    queryKey: ['fetchScheduledGifts', params],
    queryFn: () => scheduledGiftsService.fetchScheduledGifts(params),
    enabled: enabled && !!params.companyId,
    keepPreviousData: true,
    refetchInterval: (data) =>
      data?.list.some((gift) => gift.status === 'PROCESSING') ? PROCESSING_REFRESH_MS : false,
  });
};

const useFetchScheduledGift = (id: string | undefined) => {
  return useQuery<ScheduledGift, Error>({
    queryKey: ['fetchScheduledGift', id],
    queryFn: () => scheduledGiftsService.fetchScheduledGift(id as string),
    enabled: !!id,
    refetchInterval: (data) => (data?.status === 'PROCESSING' ? PROCESSING_REFRESH_MS : false),
  });
};

const useFetchScheduledGiftRecipients = (
  id: string | undefined,
  params: FetchScheduledGiftRecipientsRequest
) => {
  return useQuery<FetchScheduledGiftRecipientsResponse, Error>({
    queryKey: ['fetchScheduledGiftRecipients', id, params],
    queryFn: () => scheduledGiftsService.fetchScheduledGiftRecipients(id as string, params),
    enabled: !!id,
    keepPreviousData: true,
  });
};

const useCreateScheduledGift = (
  options: UseMutationOptions<ScheduledGift, Error, CreateScheduledGiftRequest> = {}
) => {
  const invalidate = useInvalidateScheduledGifts();

  return useMutation<ScheduledGift, Error, CreateScheduledGiftRequest>(
    (body) => scheduledGiftsService.createScheduledGift(body),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate();
        options.onSuccess?.(data, variables, context);
      },
    }
  );
};

const useUpdateScheduledGift = (
  options: UseMutationOptions<
    ScheduledGift,
    Error,
    { id: string; body: UpdateScheduledGiftRequest }
  > = {}
) => {
  const invalidate = useInvalidateScheduledGifts();

  return useMutation<ScheduledGift, Error, { id: string; body: UpdateScheduledGiftRequest }>(
    ({ id, body }) => scheduledGiftsService.updateScheduledGift(id, body),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate();
        options.onSuccess?.(data, variables, context);
      },
    }
  );
};

const useCancelScheduledGift = (options: UseMutationOptions<ScheduledGift, Error, string> = {}) => {
  const invalidate = useInvalidateScheduledGifts();

  return useMutation<ScheduledGift, Error, string>(
    (id) => scheduledGiftsService.cancelScheduledGift(id),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate();
        options.onSuccess?.(data, variables, context);
      },
    }
  );
};

const useRetryScheduledGift = (options: UseMutationOptions<ScheduledGift, Error, string> = {}) => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateScheduledGifts();

  return useMutation<ScheduledGift, Error, string>(
    (id) => scheduledGiftsService.retryScheduledGift(id),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate();
        // A retry spends points, so the company balance changes too.
        queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
        options.onSuccess?.(data, variables, context);
      },
    }
  );
};

/**
 * A gift's recipients, optionally only those with the given outcomes, as the employees the gift flow can
 * pre-select. Only people the gift flow's employee list shows come back: someone who has left since drops out.
 */
export const fetchGiftRecipientsAsEmployees = async (
  gift: Pick<ScheduledGift, 'id' | 'companyId'>,
  {
    outcomes,
    onlyPayableNow = false,
  }: {
    outcomes?: ScheduledGiftRecipientOutcome[];
    /** Leave out people on unpaid leave too, for a gift that may be sent straight away. */
    onlyPayableNow?: boolean;
  } = {}
): Promise<Individual[]> => {
  const individualIds: string[] = [];
  let page = 0;
  let total = 0;
  do {
    // eslint-disable-next-line no-await-in-loop
    const recipients = await scheduledGiftsService.fetchScheduledGiftRecipients(gift.id, {
      outcomes,
      page,
      size: RECIPIENT_LOOKUP_PAGE_SIZE,
    });
    individualIds.push(...recipients.list.map((recipient) => recipient.individualId));
    total = recipients.total;
    page += 1;
  } while (page * RECIPIENT_LOOKUP_PAGE_SIZE < total);

  const batches: string[][] = [];
  for (let index = 0; index < individualIds.length; index += RECIPIENT_LOOKUP_PAGE_SIZE) {
    batches.push(individualIds.slice(index, index + RECIPIENT_LOOKUP_PAGE_SIZE));
  }

  const employees: Individual[] = [];
  for (let index = 0; index < batches.length; index += 1) {
    // eslint-disable-next-line no-await-in-loop
    const people = await individualsService.fetchIndividuals({
      ids: batches[index],
      companyId: [gift.companyId],
      role: ['EMPLOYEE'],
      // The same people step 1 of the gift flow lists.
      status: ['CREATED', 'INVITED', 'ACTIVE'],
      onUnpaidLeave: onlyPayableNow ? false : undefined,
      page: 0,
      size: RECIPIENT_LOOKUP_PAGE_SIZE,
    });
    employees.push(...people.list);
  }

  return employees;
};

/**
 * The people whose payment from a gift failed and who can be paid now, to send the gift to again. Skipped people
 * (e.g. leavers) are left out on purpose, and so is anyone on unpaid leave, since sending now to them would reject
 * the whole gift.
 */
export const fetchFailedRecipientsAsEmployees = (gift: Pick<ScheduledGift, 'id' | 'companyId'>) =>
  fetchGiftRecipientsAsEmployees(gift, { outcomes: ['FAILED'], onlyPayableNow: true });

/**
 * A gift's recipients to pre-select when editing it. People on unpaid leave are left out, as step 1 doesn't list
 * them and they couldn't be seen or unticked there.
 */
const useFetchGiftRecipientsAsEmployees = (
  gift: Pick<ScheduledGift, 'id' | 'companyId'> | undefined,
  enabled = true
) => {
  return useQuery<Individual[], Error>({
    queryKey: ['fetchScheduledGiftRecipientsAsEmployees', gift?.id],
    queryFn: () =>
      fetchGiftRecipientsAsEmployees(gift as Pick<ScheduledGift, 'id' | 'companyId'>, {
        onlyPayableNow: true,
      }),
    enabled: enabled && !!gift,
    // Loaded once per visit, so a refetch can't reset the employees the admin has since changed.
    staleTime: Infinity,
    cacheTime: 0,
  });
};

const useScheduledGifts = {
  useFetchScheduledGifts,
  useFetchScheduledGift,
  useFetchScheduledGiftRecipients,
  useFetchGiftRecipientsAsEmployees,
  useCreateScheduledGift,
  useUpdateScheduledGift,
  useCancelScheduledGift,
  useRetryScheduledGift,
};

export default useScheduledGifts;
